import Vue from "vue";
import Vuex from "vuex";
import orderBy from "lodash/orderBy";
import flatMap from "lodash/flatMap";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import filter from "lodash/filter";

import SearchParamsDesktop from "../../components/Search/SearchParamsDesktop";
import SearchParamsMobile from "../../components/Search/SearchParamsMobile";
import Responsive from "../../components/core/Responsive";
import YachtResultPanel from "../../components/Search/YachtResultPanel";
import YachtResultSummary from "../../components/Search/YachtResultSummary";
import MobileFilters from "../../components/Search/MobileFilters/Yacht";
import YachtBudgetFilter from "../../components/Search/DesktopFilters/YachtBudgetFilter";
import YachtCapacityFilter from "../../components/Search/DesktopFilters/YachtCapacityFilter";
import YachtDestinationFilter from "../../components/Search/DesktopFilters/YachtDestinationFilter";
import YachtSizeFilter from "../../components/Search/DesktopFilters/YachtSizeFilter";
import YachtInclusionFilter from "../../components/Search/DesktopFilters/YachtInclusionFilter";
import YachtClearFiltersButton from "../../components/Search/YachtClearFiltersButton";
import ZbModal from "../../components/core/ZbModal";
import SearchStickyNav from "../../components/Search/SearchStickyNav";
import currencyRanges from "../../helpers/currencyRanges.json";

import {
  getCountryFilters,
  getBudgetFilters,
  getYachtSizeFilters,
  getYachtCapacityFilters,
  applyFilterURL,
} from "../../helpers/yachtSearchHelpers";
import {
  syncURLParams,
  searchWithDelay,
  getBudgetsFilter,
} from "../../helpers/searchHelpers";
import get from "lodash/get";
import countBy from "lodash/countBy";

const algoliasearchV4 = require("algoliasearch");

Vue.component("search-params-desktop", SearchParamsDesktop);
Vue.component("search-params-mobile", SearchParamsMobile);
Vue.component("mobile-filters", MobileFilters);
Vue.component("responsive", Responsive);
Vue.component("zb-modal", ZbModal);
Vue.component("yacht-result-panel", YachtResultPanel);
Vue.component("yacht-result-summary", YachtResultSummary);
Vue.component("yacht-budget-filter", YachtBudgetFilter);
Vue.component("yacht-capacity-filter", YachtCapacityFilter);
Vue.component("yacht-destination-filter", YachtDestinationFilter);
Vue.component("yacht-size-filter", YachtSizeFilter);
Vue.component("yacht-inclusion-filter", YachtInclusionFilter);
Vue.component("yacht-clear-filters-button", YachtClearFiltersButton);
Vue.component("search-sticky-nav", SearchStickyNav);

Vue.use({
  install(Vue) {
    const Algolia = algoliasearchV4(
      window.zubluAlgolia.applicationID,
      window.zubluAlgolia.publicAPIKey
    );
    const yatchIndex = Algolia.initIndex(window.zubluAlgolia.yachtIndex);

    Vue.prototype.$algolia = {
      instance: Algolia,
      searchAllYachts: function({ facetFilters, numericFilters }) {
        return new Promise((resolve, reject) => {
          yatchIndex
            .search("", {
              facetFilters,
              numericFilters,
              hitsPerPage: 1000,
            })
            .then(resolve)
            .catch(reject);
        });
      },
    };
  },
});

Vue.use(Vuex);
const AVAILABLE_RANKINGS = ["price", "occupancy", "special-offer"];
const plugin = (store) => {
  window.addEventListener("onCurrencyChanged", (e) => {
    store.commit("setCurrency", e.detail.currency);
    store.dispatch("loadStatistics");
    store.dispatch("loadSuppliers");
    applyFilterURL(store.state);
  });
};

const store = new Vuex.Store({
  state() {
    const searchParams = new URLSearchParams(window.location.search);

    let page = 1;
    if (searchParams.has("page") && searchParams.get("page") !== "1") {
      page = Number(searchParams.get("page"));

      if (Number.isNaN(page)) {
        page = 1;
      }
    }

    let ranking = "price";
    if (
      searchParams.has("sort") &&
      AVAILABLE_RANKINGS.includes(searchParams.get("sort"))
    ) {
      ranking = searchParams.get("sort");
    }

    if (searchParams.has("budgets")) {
      const budgets = getBudgetsFilter("yacht");
      window.zubluSearchParams.budgets = budgets;
    }

    return {
      filters: window.zubluSearchParams,
      destination: window.zubluDestination,
      page,
      ranking: ranking,
      yachtsResult: [],
      statistics: null,
      availability: {},
      isLoading: true,
      currency: window.topNavStore.state.currency,
    };
  },
  getters: {
    secondaryFilteredResults(state) {
      const selectedDestinations = state.filters.destinations
        .split(",")
        .filter((item) => item);

      const selectedInclusions = state.filters.facilities
        .split(",")
        .filter((x) => x);

      let filtered = state.yachtsResult
        .filter((yacht) => {
          if (selectedDestinations.length === 0) {
            return true;
          }

          return yacht.__odyssey_itineraries.some((itinerary) => {
            return itinerary.charter_itinerary.destination_tagging.some(
              (destCode) => {
                return selectedDestinations.includes(destCode);
              }
            );
          });
        })
        .filter((yacht) => {
          if (selectedInclusions.length === 0) {
            return true;
          }

          return yacht.__odyssey_inclusions.some((incl) => {
            return selectedInclusions.includes(incl.id.toString());
          });
        });

      const searchParams = new URLSearchParams(window.location.search);

      if (searchParams.has("month")) {
        let queryMonth = searchParams.get("month").split("-")[1];
        queryMonth = moment()
          .month(queryMonth)
          .format("MMMM")
          .toLowerCase();

        filtered = filtered.filter((yacht) => {
          return yacht.__odyssey_itineraries.some((itinerary) => {
            const seasons = JSON.parse(itinerary.season);
            return seasons.includes(queryMonth);
          });
        });
      }

      return filtered;
    },
    sortedResults(state, getters) {
      // price by default
      let sorter = [
        "__odyssey_price_per_night",
        "__odyssey_max_occupancy",
        "__odyssey_zublu_featured",
      ];
      let sortdir = ["asc", "asc", "desc"];

      if (state.ranking === "occupancy") {
        sorter = [
          "__odyssey_max_occupancy",
          "__odyssey_price_per_night",
          "__odyssey_zublu_featured",
        ];
        sortdir = ["asc", "asc", "desc"];
      }
      if (state.ranking === "special-offer") {
        sorter = [
          "__odyssey_price_per_night",
          "__odyssey_max_occupancy",
          "__odyssey_zublu_featured",
        ];
        sortdir = ["asc", "asc", "desc"];
      }

      return orderBy(getters.secondaryFilteredResults, sorter, sortdir);
    },
    availableDestinations(state) {
      if (state.statistics === null) {
        return [];
      }

      let destinationCodes = [];

      for (const yacht of state.statistics) {
        const itineraries = flatMap(
          yacht.__odyssey_itineraries,
          (itinerary) => {
            return itinerary.charter_itinerary;
          }
        );

        const destinations = flatMap(
          itineraries,
          (itinerary) => itinerary.destination_tagging
        );

        destinationCodes = destinationCodes.concat(destinations);
      }

      destinationCodes = uniq(destinationCodes);
      const allDestinationsFromCraft = flatMap(
        window.zubluDestination.children,
        (province) => {
          return province.children;
        }
      );

      const result = [];
      for (const code of destinationCodes) {
        const destinationObj = allDestinationsFromCraft.find(
          (d) => d.code === code
        );

        if (destinationObj) {
          result.push(destinationObj);
        }
      }

      return result;
    },
    availableInclusions(state) {
      let inclusions = flatMap(
        state.yachtsResult || [],
        (programme) => programme.__odyssey_inclusions
      );

      inclusions = filter(inclusions, ["filterable", true]);

      inclusions = uniqBy(inclusions, (incl) => incl.id);

      inclusions = orderBy(inclusions, ["order"], ["asc"]);

      return inclusions;
    },
    availableBudgetOptions(state) {
      return get(state.availability, ["visibleBudgets"], {});
    },
  },
  mutations: {
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setResult(state, yachtsResult) {
      state.yachtsResult = yachtsResult;
    },
    setStatistics(state, statistics) {
      state.statistics = statistics;
    },
    setFilters(state, filters) {
      state.filters = filters;
      applyFilterURL(state);
    },
    setFilter(state, { name, value }) {
      state.filters[name] = value;
      applyFilterURL(state);
    },
    setRanking(state, ranking) {
      state.ranking = ranking;

      const searchParams = new URLSearchParams(window.location.search);
      if (state.ranking !== "price") {
        searchParams.set("sort", state.ranking);
      } else {
        searchParams.delete("sort");
      }

      syncURLParams(searchParams);
    },
    setPage(state, page) {
      state.page = page;
      const searchParams = new URLSearchParams(window.location.search);

      if (state.page !== 1) {
        searchParams.set("page", state.page);
      } else {
        searchParams.delete("page");
      }

      syncURLParams(searchParams);
    },
    clearFilters(state) {
      state.filters = {
        budgets: "",
        destinations: "",
        facilities: "",
        yachtCapacities: "",
        yachtSizes: "",
      };
      state.page = 1;

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete("budgets");
      searchParams.delete("destinations");
      searchParams.delete("page");

      syncURLParams(searchParams);
    },
    setAvailability(state, payload) {
      state.availability = payload;
    },
    setCurrency(state, value) {
      state.currency = value;
    },
  },
  actions: {
    loadSuppliers(context) {
      context.commit("setIsLoading", true);

      const facetFilters = [
        getCountryFilters(context.state.filters),
        "__platform_status:live",
      ].filter((f) => f);

      const numericFilters = [
        getBudgetFilters(context.state.filters),
        getYachtSizeFilters(context.state.filters),
        getYachtCapacityFilters(context.state.filters),
      ].filter((f) => f.length);

      return searchWithDelay(
        this._vm.$algolia.searchAllYachts,
        0
      )({ facetFilters, numericFilters })
        .then((result) => {
          const yachts = result.hits;

          context.commit("setResult", yachts);
        })
        .catch((error) => {
          console.log(error);
        })
        .then(() => {
          context.commit("setIsLoading", false);
        });
    },
    loadStatistics(context) {
      const facetFilters = [getCountryFilters(context.state.filters)].filter(
        (f) => f
      );

      this._vm.$algolia
        .searchAllYachts({
          facetFilters: [facetFilters, "__platform_status:live"],
          numericFilters: "__odyssey_price_per_night > 0",
        })
        .then((result) => {
          const { currency } = context.state;
          const rangeInSelectedCurrency =
            currencyRanges[currency.code].yacht.filterRange;
          const rate = window.zubluCurrencyTable[currency.code];

          const visibleBudgets = countBy(result.hits, (hit) => {
            if (
              hit.__odyssey_price_per_night <=
              rangeInSelectedCurrency[0][1] * rate
            ) {
              return "min0";
            } else if (
              hit.__odyssey_price_per_night <=
              rangeInSelectedCurrency[1000][1] * rate
            ) {
              return "min1000";
            } else if (
              hit.__odyssey_price_per_night <=
              rangeInSelectedCurrency[2000][1] * rate
            ) {
              return "min2000";
            }

            return "min4000";
          });

          context.commit("setStatistics", result.hits);
          context.commit("setAvailability", { visibleBudgets });
        });
    },
  },
  plugins: [plugin],
});

const apps = document.querySelectorAll(".vue-app");

Array.prototype.forEach.call(
  apps,
  (el) =>
    new Vue({
      el,
      template: el.innerHTML,
      store,
    })
);
